/**
 * CreativeViewer — fixed-position left pane for the Creative Workspace
 * split layout. Contains the media block, copy block, ad deployment block
 * (ingested only), and the Phase 0 sibling strip.
 *
 * The pane is `position: sticky; top: 72px` (matching the identity bar
 * height) so it stays in view while the right-pane analysis panel scrolls.
 */

import { useState, type ReactNode } from 'react';
import { Copy, Eye, Play } from 'lucide-react';
import { clsx } from 'clsx';
import type { CreativeWorkspaceData } from './types';

interface Props {
  creative: CreativeWorkspaceData['creative'];
  bestMediaUrl: string | null | undefined;
  siblingStrip?: ReactNode;
}

export function CreativeViewer({ creative, bestMediaUrl, siblingStrip }: Props) {
  const c = creative as Record<string, unknown>;
  const form = (c.form ?? {}) as {
    headline?: string;
    primary_text?: string;
    cta_type?: string;
    media_type?: string;
    image_url?: string;
    thumbnail_url?: string;
    video_url?: string;
    video_gcs_path?: string;
  };
  const isVideo = Boolean(form.video_url || form.video_gcs_path);
  const thumb = bestMediaUrl ?? form.thumbnail_url ?? form.image_url;

  const qualityScore = numOrNull((c as Record<string, unknown>).quality_score);
  const brandCompliance = numOrNull((c as Record<string, unknown>).brand_compliance);
  const qualityPct = qualityScore != null ? Math.round(qualityScore * 100) : null;
  const brandPct = brandCompliance != null ? Math.round(brandCompliance * 100) : null;

  return (
    <div className="lg:sticky lg:top-[72px] lg:h-[calc(100vh-72px)] lg:overflow-y-auto flex flex-col gap-4 px-5 py-4 border-r border-slate-100">
      <div className="relative rounded-2xl overflow-hidden bg-slate-100 flex-shrink-0 aspect-square max-w-md mx-auto w-full">
        {thumb && /^https?:/i.test(thumb) ? (
          <img
            src={thumb}
            alt={form.headline ?? 'Creative'}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Eye size={32} className="text-slate-300" />
          </div>
        )}
        {isVideo && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none">
            <div className="bg-white/90 rounded-full p-3 shadow-lg">
              <Play size={20} className="text-navy-900 fill-navy-900 ml-0.5" />
            </div>
          </div>
        )}

        {qualityPct != null && (
          <ScoreBadge value={qualityPct} label="Quality" position="top-right" />
        )}
        {brandPct != null && (
          <ScoreBadge value={brandPct} label="Brand" position="bottom-right" />
        )}
      </div>

      <CopyBlock form={form} />

      {(c.ad_id as string | undefined) && (
        <AdDeploymentBlock
          adId={c.ad_id as string}
          campaignName={c.campaign_name as string | undefined}
          adsetName={c.adset_name as string | undefined}
          platform={c.platform as string | undefined}
        />
      )}

      {siblingStrip}
    </div>
  );
}

function numOrNull(v: unknown): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function ScoreBadge({
  value,
  label,
  position,
}: {
  value: number;
  label: string;
  position: 'top-right' | 'bottom-right';
}) {
  const color = value >= 90 ? 'bg-emerald-500' : value >= 70 ? 'bg-amber-500' : 'bg-rose-500';
  const pos = position === 'top-right' ? 'top-2 right-2' : 'bottom-2 right-2';
  return (
    <div
      className={`absolute ${pos} ${color} text-white text-[9px] font-mono rounded-md px-1.5 py-0.5 shadow-sm`}
    >
      {label} {value}%
    </div>
  );
}

function CopyBlock({
  form,
}: {
  form: { headline?: string; primary_text?: string; cta_type?: string };
}) {
  const headline = form.headline?.trim();
  const primary = form.primary_text?.trim();
  const cta = form.cta_type?.trim();

  if (!headline && !primary && !cta) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
      {headline && <CopyRow label="HEADLINE" value={headline} />}
      {primary && <CopyRow label="PRIMARY" value={primary} multiline />}
      {cta && <CopyRow label="CTA" value={cta} highlight />}
    </div>
  );
}

function CopyRow({
  label,
  value,
  multiline = false,
  highlight = false,
}: {
  label: string;
  value: string;
  multiline?: boolean;
  highlight?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const truncated = multiline && !expanded && value.length > 160;
  const display = truncated ? `${value.slice(0, 160)}…` : value;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard unavailable; ignore */
    }
  };

  return (
    <div className="group flex items-start gap-2 px-3 py-2">
      <span className="text-[9px] font-mono uppercase tracking-wider text-slate-400 w-14 mt-1 flex-shrink-0">
        {label}
      </span>
      <p
        className={clsx(
          'text-xs leading-relaxed flex-1 min-w-0',
          highlight ? 'text-teal-600 font-medium' : 'text-slate-700',
          multiline ? 'whitespace-pre-line' : 'truncate',
        )}
      >
        {display}
        {truncated && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="ml-1 text-[10px] font-mono text-teal-600 hover:underline"
          >
            more
          </button>
        )}
      </p>
      <button
        type="button"
        onClick={onCopy}
        title={copied ? 'Copied' : 'Copy'}
        className="text-slate-300 group-hover:text-teal-500 transition-colors flex-shrink-0 mt-0.5"
      >
        <Copy size={11} />
      </button>
    </div>
  );
}

function AdDeploymentBlock({
  adId,
  campaignName,
  adsetName,
  platform,
}: {
  adId: string;
  campaignName?: string;
  adsetName?: string;
  platform?: string;
}) {
  const isMeta = (platform ?? '').toLowerCase().includes('meta') || (platform ?? '').toLowerCase().includes('facebook');
  const metaUrl = isMeta ? `https://business.facebook.com/adsmanager/manage/ads?selected_ad_ids=${encodeURIComponent(adId)}` : null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-1 text-xs">
      <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
        Ad Deployment
      </p>
      {campaignName && (
        <DeployRow label="Campaign" value={campaignName} />
      )}
      {adsetName && <DeployRow label="Ad set" value={adsetName} />}
      <DeployRow label="Ad ID" value={adId} mono />
      {metaUrl && (
        <a
          href={metaUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-block text-[11px] text-teal-600 hover:text-teal-700 hover:underline pt-1"
        >
          View in Meta →
        </a>
      )}
    </div>
  );
}

function DeployRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-slate-400 flex-shrink-0">{label}</span>
      <span
        className={clsx(
          'truncate text-right text-navy-900',
          mono && 'font-mono text-[11px]',
        )}
      >
        {value}
      </span>
    </div>
  );
}
